import React, { useState, useEffect } from 'react';
import {
  KeyRound, Shield, Users, Settings, Eye, Edit, Trash2, Plus,
  Check, X, RefreshCw, AlertTriangle, Lock, Unlock, Search,
  ChevronRight, Database, BarChart3, FileText, CreditCard
} from 'lucide-react';
import { adminService } from '../services/adminService';
import { Role } from '../types';
import { useToast } from '../contexts/ToastContext';
import './Roles.css';

interface PermissionGroup {
  name: string;
  icon: React.ReactNode;
  permissions: string[];
  description: string;
}

/** Pull the backend's message out of an axios error without leaking [object Object]. */
const readError = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: unknown } })?.response?.data;
  if (typeof detail === 'string') return detail;
  const body = detail as { message?: string; detail?: unknown } | undefined;
  if (body?.message) return body.message;
  if (typeof body?.detail === 'string') return body.detail;
  const nested = body?.detail as { message?: string } | undefined;
  if (nested?.message) return nested.message;
  return fallback;
};

const GROUP_ICONS: Record<string, React.ReactNode> = {
  Users: <Users size={18} />,
  Games: <Database size={18} />,
  Transactions: <CreditCard size={18} />,
  Withdrawals: <FileText size={18} />,
  Configuration: <Settings size={18} />,
  Analytics: <BarChart3 size={18} />,
  Security: <Shield size={18} />,
  Roles: <KeyRound size={18} />,
};

const Roles: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  // The permission catalogue is served by the backend (app/rbac/catalogue.py).
  // Rendering a hardcoded copy here would mean a permission added on the server
  // silently never appears in this matrix — the same class of drift that let
  // this page claim a permission model that did not exist.
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);



  useEffect(() => {
    loadCatalogue();
    loadRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCatalogue = async () => {
    try {
      const data = await adminService.getPermissionCatalogue();
      setPermissionGroups(
        data.groups.map((g) => ({
          name: g.name,
          icon: GROUP_ICONS[g.name] ?? <Check size={18} />,
          permissions: g.permissions.map((p) => p.code),
          description: g.permissions.map((p) => p.description).join(' · '),
        })),
      );
    } catch (err) {
      setLoadError(readError(err, 'Could not load the permission catalogue.'));
    }
  };

  const loadRoles = async () => {
    // No invented fallback here on purpose. This page used to synthesise roles
    // when the request failed, which made a permission model look real when
    // none existed. An error must read as an error.
    try {
      setLoading(true);
      setLoadError('');
      const data = await adminService.getRoles();
      setRoles(data);
      if (selectedRole) {
        const refreshed = data.find((r) => r.id === selectedRole.id) || null;
        setSelectedRole(refreshed);
        setPermissions(refreshed ? refreshed.permissions : []);
      }
    } catch (err) {
      setRoles([]);
      setLoadError(readError(err, 'Could not load roles.'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) {
      toast.warning('Validation', 'Please enter a role name');
      return;
    }

    try {
      setSaving(true);
      const created = await adminService.createRole({
        name: newRoleName.trim(),
        description: newRoleDescription.trim() || undefined,
        permissions: [],
      });
      setRoles([...roles, created]);
      setNewRoleName('');
      setNewRoleDescription('');
      setShowCreateModal(false);
      toast.success('Role Created', `${created.name} role has been created`);
    } catch (err) {
      toast.error('Could Not Create Role', readError(err, 'The role was not created.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (roleId: number) => {
    const role = roles.find((r) => r.id === roleId);
    if (!role) return;
    if (role.is_system) {
      toast.error('Cannot Delete', 'This is a built-in role and cannot be deleted');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete the "${role.name}" role?`)) {
      return;
    }
    try {
      await adminService.deleteRole(roleId);
      setRoles(roles.filter((r) => r.id !== roleId));
      if (selectedRole?.id === roleId) {
        setSelectedRole(null);
        setPermissions([]);
      }
      toast.success('Role Deleted', 'The role has been removed');
    } catch (err) {
      // The backend refuses to delete a role that still has users; surface
      // that rather than removing it from the list and lying about it.
      toast.error('Could Not Delete Role', readError(err, 'The role was not deleted.'));
    }
  };

  const handleTogglePermission = (permission: string) => {
    if (!selectedRole) return;
    if (selectedRole.is_system) {
      toast.warning(
        'Built-in Role',
        `${selectedRole.name} is defined by the backend catalogue and is reset on every deploy.`,
      );
      return;
    }

    const newPermissions = permissions.includes(permission)
      ? permissions.filter((p) => p !== permission)
      : [...permissions, permission];

    setPermissions(newPermissions);
  };

  const handleToggleGroup = (group: PermissionGroup) => {
    if (!selectedRole) return;
    if (selectedRole.is_system) {
      toast.warning(
        'Built-in Role',
        `${selectedRole.name} is defined by the backend catalogue and is reset on every deploy.`,
      );
      return;
    }

    const hasAll = group.permissions.every((p) => permissions.includes(p));
    const newPermissions = hasAll
      ? permissions.filter((p) => !group.permissions.includes(p))
      : Array.from(new Set([...permissions, ...group.permissions]));

    setPermissions(newPermissions);
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;
    try {
      setSaving(true);
      const updated = await adminService.setRolePermissions(selectedRole.id, permissions);
      setRoles(roles.map((r) => (r.id === updated.id ? updated : r)));
      setSelectedRole(updated);
      setPermissions(updated.permissions);
      toast.success('Permissions Saved', `Updated permissions for ${updated.name}`);
    } catch (err) {
      toast.error('Could Not Save', readError(err, 'Permissions were not changed.'));
    } finally {
      setSaving(false);
    }
  };

  const getPermissionIcon = (action: string) => {
    switch (action) {
      case 'read':
        return <Eye size={12} />;
      case 'write':
        return <Edit size={12} />;
      case 'delete':
        return <Trash2 size={12} />;
      default:
        return <Check size={12} />;
    }
  };

  const filteredRoles = roles.filter((r) =>
    r.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="roles-page-modern">
      {/* Header */}
      <div className="roles-header">
        <div className="header-left">
          <h1><KeyRound size={28} /> Roles & Permissions</h1>
          <p className="header-subtitle">Manage access control and permissions</p>
        </div>
        <div className="header-actions">
          <button onClick={loadRoles} className="refresh-btn" disabled={loading}>
            <RefreshCw className={loading ? 'spin' : ''} size={18} />
            Refresh
          </button>
          <button onClick={() => setShowCreateModal(true)} className="create-btn">
            <Plus size={18} /> Create Role
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="roles-container-modern">
        {/* Roles List */}
        <div className="roles-sidebar">
          <div className="sidebar-header">
            <h3>Roles</h3>
            <span className="role-count">{roles.length} roles</span>
          </div>

          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search roles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {loadError && (
            <div className="roles-load-error" role="alert">
              {loadError}
            </div>
          )}

          <div className="roles-list-modern">
            {loading ? (
              <div className="loading-state">
                <RefreshCw className="spin" size={24} />
                <span>Loading roles...</span>
              </div>
            ) : filteredRoles.length > 0 ? (
              filteredRoles.map((role) => (
                <div
                  key={role.id}
                  className={`role-card ${selectedRole?.id === role.id ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedRole(role);
                    setPermissions(role.permissions || []);
                  }}
                >
                  <div className="role-card-content">
                    <div className="role-icon">
                      {role.name === 'Super Admin' ? <Shield size={20} /> : <KeyRound size={20} />}
                    </div>
                    <div className="role-info">
                      <span className="role-name">{role.name}</span>
                      <span className="role-desc">{role.description || 'No description'}</span>
                    </div>
                  </div>
                  <div className="role-meta">
                    <span className="permission-count">
                      {role.permissions?.length || 0} permissions
                    </span>
                    <ChevronRight size={16} />
                  </div>
                  {role.name !== 'Super Admin' && role.name !== 'User' && (
                    <button
                      className="delete-role-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteRole(role.id);
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))
            ) : (
              <div className="no-roles">No roles found</div>
            )}
          </div>
        </div>

        {/* Permissions Panel */}
        <div className="permissions-panel-modern">
          {selectedRole ? (
            <>
              <div className="panel-header">
                <div className="selected-role-info">
                  <div className="role-badge">
                    {selectedRole.name === 'Super Admin' ? (
                      <Lock size={16} />
                    ) : (
                      <Unlock size={16} />
                    )}
                    <span>{selectedRole.name}</span>
                  </div>
                  <p className="role-description">{selectedRole.description || 'Configure permissions for this role'}</p>
                </div>
                <button
                  onClick={handleSavePermissions}
                  className="save-btn"
                  disabled={selectedRole.is_system || saving}
                  title={
                    selectedRole.is_system
                      ? 'Built-in roles are defined by the backend catalogue'
                      : undefined
                  }
                >
                  <Check size={18} /> {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>

              <div className="permissions-groups">
                {permissionGroups.map((group) => {
                  const groupPermissionsCount = group.permissions.filter((p) =>
                    permissions.includes(p)
                  ).length;
                  const hasAll = groupPermissionsCount === group.permissions.length;
                  const hasNone = groupPermissionsCount === 0;

                  return (
                    <div key={group.name} className="permission-group">
                      <div className="group-header">
                        <div className="group-title">
                          <span className="group-icon">{group.icon}</span>
                          <div>
                            <h4>{group.name}</h4>
                            <span className="group-desc">{group.description}</span>
                          </div>
                        </div>
                        <div className="group-actions">
                          <span className="group-count">
                            {groupPermissionsCount}/{group.permissions.length}
                          </span>
                          <button
                            className={`toggle-all-btn ${hasAll ? 'active' : ''}`}
                            onClick={() => handleToggleGroup(group)}
                            disabled={selectedRole.name === 'Super Admin'}
                          >
                            {hasAll ? 'Remove All' : hasNone ? 'Grant All' : 'Toggle'}
                          </button>
                        </div>
                      </div>
                      <div className="permissions-list">
                        {group.permissions.map((permission) => {
                          const [, action] = permission.split('.');
                          const isChecked = permissions.includes(permission);

                          return (
                            <label
                              key={permission}
                              className={`permission-item ${isChecked ? 'checked' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleTogglePermission(permission)}
                                disabled={selectedRole.name === 'Super Admin'}
                              />
                              <span className="checkmark">
                                {isChecked && <Check size={12} />}
                              </span>
                              <span className="permission-icon">{getPermissionIcon(action)}</span>
                              <span className="permission-name">{permission}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {selectedRole.name === 'Super Admin' && (
                <div className="super-admin-notice">
                  <AlertTriangle size={18} />
                  <span>Super Admin role has all permissions and cannot be modified</span>
                </div>
              )}
            </>
          ) : (
            <div className="no-selection">
              <div className="empty-state-icon">
                <Shield size={48} />
              </div>
              <h3>Select a Role</h3>
              <p>Choose a role from the list to manage its permissions</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Role Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><Plus size={20} /> Create New Role</h3>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Role Name</label>
                <input
                  type="text"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="e.g., Content Moderator"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newRoleDescription}
                  onChange={(e) => setNewRoleDescription(e.target.value)}
                  placeholder="Brief description of this role..."
                  className="form-input"
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowCreateModal(false)} className="cancel-btn">
                Cancel
              </button>
              <button onClick={handleCreateRole} className="create-btn">
                <Plus size={18} /> Create Role
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roles;
